import React, { useState, useCallback } from "react";

import { selectTranslations } from "../../../../src/app/core/translateServiceModule/TranslationsSelector";

interface EDeliveryComponentProps {
    action: (
        event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>,
        selectedValue: string | null,
        isEmailRequired: boolean,
        isTaxFormRequired: boolean
    ) => void;
    eDeliveryInitData: {
        allowedToOverwrite: string;
        docTypes: string;
        eDeliveryOption: string;
        econsentemailAddress: string | null;
        isAtleaseOneActiveThere: string;
        planName: string;
        pptConsent: string;
        secondaryEmailAddress: string | null;
    };
}
// All possible communication preference values
const CommunicationPreferences = {
    EDELIVERY_ONLY: "YES_CONSENT_ANY_ELECTRONIC", // The participant has chosen "e-delivery"
    EDELIVERY_AND_MAIL: "YES_CONSENT_ALL_MAIL", // The participant has chosen "e-delivery and by mail"
    ALLOW_DEFAULT_EDELIVERY: "NO_CONSENT_ANY_ELECTRONIC", // The participant has not chosen a preference, but the plan defaults to "e-delivery"
    NO_CONSENT_ALL_MAIL: "NO_CONSENT_ALL_MAIL" // The participant has not chosen a preference, but the plan defaults to "e-delivery and by mail"
};

const getEmailChoices = (data: {
    allowedToOverwrite?: string;
    docTypes?: string;
    eDeliveryOption?: string;
    econsentemailAddress: any;
    isAtleaseOneActiveThere?: string;
    planName?: string;
    pptConsent?: string;
    secondaryEmailAddress: any;
}) => {
    const emails: Array<string> = [];
    const econsentEmail = data?.econsentemailAddress;
    const secondaryEmail = data?.secondaryEmailAddress;

    if (econsentEmail) {
        emails.push(econsentEmail);
    }

    if (secondaryEmail) {
        emails.push(secondaryEmail);
    }
    return emails;
};

const validateEmailAddress = (email: string) => {
    return String(email)
        .toLowerCase()
        .match(
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        );
};

const EDeliveryInlineView = (props: EDeliveryComponentProps) => {
    const { action, eDeliveryInitData } = props;
    const [selectedEmail, setSelectedEmail] = useState(eDeliveryInitData.econsentemailAddress);
    const [communicationPreference, setSelectedEDeliveryOption] = useState(
        CommunicationPreferences.EDELIVERY_ONLY
    );
    const [checked, setChecked] = useState(true);
    const [error, setError] = useState(false);
    const [hasError, setHasError] = useState("");
    const [hasSuccess, setHasSuccess] = useState("");
    const [isValidEmail, setIsValidEmail] = useState(false);
    const [taxFormChecked, setTaxFormChecked] = useState(false);

    const emailChoices = getEmailChoices(eDeliveryInitData);

    const summaryTranslations = selectTranslations("summary");
    const preferenceTranslations = selectTranslations("preference");
    const profileTranslations = selectTranslations("profile");

    /**
     * the translation for 'summary.eDelivery.documentsInclude'.
     * @return {string} for the document types content.
     */
    const documentTypes = eDeliveryInitData?.docTypes;

    /**
     * Check whether or not the email address dropdown should be displayed.
     *
     * @return {Boolean} True to show the email address dropdown; false otherwise
     */
    const showEmailDropdown = emailChoices.length > 0 && (checked || taxFormChecked);

    /**
     * Update the selected email address in the factory when the participant
     * makes a change so we can access the selected value in the parent view (i.e.
     * Registration Summary or Enrollment Confirmation).
     */
    const onChangeEmail = useCallback(
        (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => {
            const selectedValue = event.target.value.trim();
            const isEmailValid = !!validateEmailAddress(selectedValue);
            setIsValidEmail(isEmailValid);

            if (selectedValue == "") {
                setError(true);
            } else {
                setError(false);
            }
            if (isEmailValid) {
                setHasSuccess(" has success");
                setError(false);
                setSelectedEmail(selectedValue);
                action(event, selectedValue, checked, taxFormChecked);
            }
            if (showEmailDropdown) {
                //dropdown
                setSelectedEmail(selectedValue);
                action(event, selectedValue, checked, taxFormChecked);
            } else {
                setSelectedEmail(selectedValue);
            }
        },
        [action, showEmailDropdown, checked, taxFormChecked]
    );

    /**
     * Update the selected e-delivery option in the factory when the participant
     * makes a change so we can access the selected value in the parent view (i.e.
     * Registration Summary or Enrollment Confirmation).
     */
    const onChangeEDelivery = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            setSelectedEDeliveryOption(communicationPreference);
            setChecked(event.target.checked);
            action(event, selectedEmail, event.target.checked, taxFormChecked);
        },
        [action, selectedEmail, communicationPreference, taxFormChecked]
    );

    /**
     * Update the selected e-delivery tax forms option in the factory when the participant
     * makes a change so we can access the selected value in the parent view (i.e.
     * Registration Summary or Enrollment Confirmation).
     */
    const onChangeEDeliveryTaxForms = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            setSelectedEDeliveryOption(communicationPreference);
            setTaxFormChecked(event.target.checked);
            action(event, selectedEmail, checked, event.target.checked);
        },
        [action, selectedEmail, communicationPreference, checked]
    );

    /**
     * Check whether or not the input to add a new email address should be displayed.
     *
     * @return {Boolean} True to show the email address input; false otherwise
     */
    const showEmailInput = emailChoices?.length === 0 || !emailChoices?.length;

    const onFocusEmailInput = (event: React.FocusEvent<HTMLInputElement, Element>) => {
        const inputValue = event.target.value.trim();
        if (inputValue === "" && event.type === "focus") {
            setError(true);
        } else {
            setError(false);
        }
        setHasError("");
    };

    const onBlurEmailInput = () => {
        if (!isValidEmail) {
            setHasError(" has-error");
        }
    };

    return (
        <>
            <div>
                <h3 className="margin-top-0">{summaryTranslations.eDelivery.title}</h3>
                <form name="eConsentForm" autoComplete="off" noValidate>
                    <div className="details-section">
                        <div className="inline-communication-preferences">
                            <div>
                                <p>{summaryTranslations.eDelivery.getEdelivery}</p>
                                <ul className="margin-left-200 margin-bottom-100">
                                    <li>{summaryTranslations.eDelivery.benefits.one}</li>
                                    <li>{summaryTranslations.eDelivery.benefits.two}</li>
                                    <li>{summaryTranslations.eDelivery.benefits.three}</li>
                                    <li>{summaryTranslations.eDelivery.benefits.four}</li>
                                </ul>
                            </div>
                            <div className="checkbox-inline">
                                <label htmlFor="communicationPreference">
                                    <input
                                        type="checkbox"
                                        name="communicationPreference"
                                        id="communicationPreference"
                                        value={communicationPreference}
                                        checked={checked}
                                        onChange={(event) => onChangeEDelivery(event)}
                                        tabIndex={0}
                                    />
                                    {summaryTranslations.eDelivery.labelEDeliveryOnly}
                                </label>
                                {documentTypes !== null && (
                                    <p>
                                        {summaryTranslations.eDelivery.documentsInclude.replace(
                                            "{{documentTypes}}",
                                            documentTypes
                                        )}
                                    </p>
                                )}
                                <label htmlFor="taxFormPreference">
                                    <input
                                        type="checkbox"
                                        name="taxFormPreference"
                                        id="taxFormPreference"
                                        checked={taxFormChecked}
                                        onChange={(event) => {
                                            onChangeEDeliveryTaxForms(event);
                                        }}
                                        tabIndex={0}
                                    />

                                    {summaryTranslations.eDelivery.labelEDeliveryOnly}
                                </label>
                                <p>
                                    Tax statement election will be in effect for any plan(s),
                                    including future plans associated with this employer. For mail
                                    delivery, leave unchecked.
                                </p>
                                {
                                    // list of emails
                                    showEmailDropdown && (
                                        <div className="form-group">
                                            <label htmlFor="emailChoice">
                                                {preferenceTranslations.eDelivery.label.email}
                                            </label>
                                            <select
                                                className="form-control"
                                                id="emailChoice"
                                                name="emailChoice"
                                                value={selectedEmail || ""}
                                                onChange={(event) => onChangeEmail(event)}
                                            >
                                                {emailChoices.map((choice) => {
                                                    return (
                                                        <option key={choice} value={choice}>
                                                            {choice}
                                                        </option>
                                                    );
                                                })}
                                            </select>
                                        </div>
                                    )
                                }
                                {
                                    // no email
                                    showEmailInput && (
                                        <div className={`form-group${hasError}${hasSuccess}`}>
                                            <label
                                                htmlFor="personalEmailInput"
                                                className="control-label"
                                            >
                                                {profileTranslations.personal.emailAddress}
                                            </label>
                                            <input
                                                type="email"
                                                name="emailChoice"
                                                id="personalEmailInput"
                                                className="form-control"
                                                value={selectedEmail || ""}
                                                onBlur={() => onBlurEmailInput()}
                                                onFocus={(event) => onFocusEmailInput(event)}
                                                required
                                                onChange={(event) => onChangeEmail(event)}
                                                maxLength={80}
                                            />
                                            {error && !isValidEmail && (
                                                <span className="message">
                                                    {
                                                        preferenceTranslations.eDelivery
                                                            .errorEmailRequired
                                                    }
                                                </span>
                                            )}
                                            {!isValidEmail &&
                                                !error &&
                                                selectedEmail !== "" &&
                                                selectedEmail !== null && (
                                                    <span className="message">
                                                        {
                                                            preferenceTranslations.eDelivery
                                                                .errorEmailInvalid
                                                        }
                                                    </span>
                                                )}
                                        </div>
                                    )
                                }
                                <div className="document-review">
                                    <p
                                        dangerouslySetInnerHTML={{
                                            __html: preferenceTranslations.eDelivery.legal.section
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </>
    );
};

export default EDeliveryInlineView;
